import { safeDivide } from "@lyfegen-platform/utils";
import { TrFun } from "apps/platform-client/client/src/utils/types";
import { round } from "lodash";
import { EMPTY_VALUE_PLACEHOLDER } from "../../../../../models/common/data";
import { FinancialDataPointStatus, Treatment } from "../../../../../models/generated/graphql";
import { formatTreatment } from "../../../../../utils/base-agreement-formatters";
import renderCurrency from "../../../../../utils/currency";
import { localeDate } from "../../../../../utils/locale-date";
import { RefundWithState } from "./map-values";
import { GetInvoiceQuery } from "../types/queries.generated";

export enum RefundState {
	Active = "ACTIVE",
	Deleted = "DELETED",
}

const getRefundInfo = (
	refund: RefundWithState,
	t: TrFun,
	currencyCode?: string | null,
	caseInstance?: GetInvoiceQuery["documents"][0]["documentRefundGroups"][0]["case"]
) => {
	const edge = refund.documentFinancialDataPointConnection.edges[0];
	const dataPoint = edge.node;
	const refundId = refund.id;
	const date = localeDate(dataPoint.prescriptionDate, { placeholder: "XX" });
	const basePrice = renderCurrency(dataPoint.basePrice || 0, currencyCode, 2, 2);
	const refundAmount = renderCurrency(dataPoint.refundable || 0, currencyCode, 2, 2);
	const pack = formatTreatment(dataPoint.treatment as Treatment) || EMPTY_VALUE_PLACEHOLDER;
	const quantity = dataPoint.quantity.toString();
	const isDelta = dataPoint.isDelta ? t("delta-Y") : t("delta-N");
	const insuranceClaimId = dataPoint.evaluates?.insuranceClaimId || EMPTY_VALUE_PLACEHOLDER;
	const externalFormId = caseInstance?.flexEntitiesConnection.edges[0]?.node.externalFormId || EMPTY_VALUE_PLACEHOLDER;
	const invoiceNumber = dataPoint.evaluates?.invoiceNumber || EMPTY_VALUE_PLACEHOLDER;
	const purchaseId = dataPoint.evaluates?.purchaseId || EMPTY_VALUE_PLACEHOLDER;
	const sourceGrossAmount = dataPoint.basePrice * dataPoint.quantity;
	const grossAmount = renderCurrency(sourceGrossAmount, currencyCode, 2, 2);
	const refundRate = `${round(safeDivide(dataPoint.refundable, sourceGrossAmount) * 100, 2)}%`;
	const predecessorIds = refund.predecessorIds?.join(", ") || EMPTY_VALUE_PLACEHOLDER;
	const canBeReplacedInDocumentIds = refund.canBeReplacedInDocumentIds?.join(", ") || EMPTY_VALUE_PLACEHOLDER;
	const canBeReplacementInDocumentIds = refund.canBeReplacementInDocumentIds?.join(", ") || EMPTY_VALUE_PLACEHOLDER;
	const successorIds = refund.successorIds?.join(", ") || EMPTY_VALUE_PLACEHOLDER;
	const wasRemoved = edge.status === FinancialDataPointStatus.Removed ? t("removed-yes") : t("removed-no");

	return {
		refundId,
		date,
		pack,
		basePrice,
		refundAmount,
		grossAmount,
		refundRate,
		quantity,
		isDelta,
		insuranceClaimId,
		invoiceNumber,
		purchaseId,
		predecessorIds,
		canBeReplacedInDocumentIds,
		canBeReplacementInDocumentIds,
		successorIds,
		wasRemoved,
		externalFormId,
	};
};

export default getRefundInfo;
